import * as XLSX from 'xlsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getLeaveApplicationById,
  getLeaveApplications,
  mapLeaveApplicationToLeaveRequest,
  reviewLeaveApplication,
} from '../api/hrmsLeaveRequests.api'
import {
  getLeavePoliciesDropdown,
} from '../api/hrmsLeavePolicies.api'
import {
  calculateDays,
  formatDateDisplay,
  LEAVE_TYPE_FILTER_OPTIONS,
} from '../constants/hrmsLeaveRequests.constants'
import type {
  LeaveApplication,
  LeaveApplicationStatus,
  LeaveApplicationsQueryParams,
  LeaveApplicationsResponse,
  ReviewLeaveApplicationPayload,
  StaffLeaveRequest,
} from '../types/hrmsLeaveRequests.types'
import { showToastMessage } from '../../../../components/Toast'
import { getApiErrorMessage } from '../../../../shared/utils/errorUtils'
import { isUuid, resolveBranchId } from '../../../../shared/utils/branchUtils'
import { useAuthStore } from '../../../auth/hooks/useAuthStore'

export interface PolicyOption {
  label: string
  value: string
}

/**
 * TanStack query hook for fetching leave applications list.
 * Query key: ['leave-applications', branchId, { page, limit, status, leavePolicyId, fromDate, toDate }]
 */
export function useLeaveRequestsListQuery(
  params: LeaveApplicationsQueryParams,
  branchId?: string
) {
  return useQuery<LeaveApplicationsResponse, unknown>({
    queryKey: [
      'leave-applications',
      branchId || '',
      {
        page: params.page || 1,
        limit: params.limit || 10,
        status: params.status || '',
        leavePolicyId: params.leavePolicyId || '',
        fromDate: params.fromDate || '',
        toDate: params.toDate || '',
      },
    ],
    queryFn: () => getLeaveApplications(params),
    placeholderData: keepPreviousData,
  })
}

/**
 * TanStack query hook for fetching a single leave application detail.
 * Query key: ['leave-application-detail', branchId, id]
 */
export function useLeaveRequestDetail(id?: string | null) {
  const { user } = useAuthStore()
  const branchId = resolveBranchId() || user?.branchId || ''

  return useQuery<LeaveApplication, unknown>({
    queryKey: ['leave-application-detail', branchId, id || ''],
    queryFn: () => {
      if (!id) {
        throw new Error('Leave application ID is required')
      }
      return getLeaveApplicationById(id)
    },
    enabled: Boolean(id),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export interface ReviewLeaveRequestOptions {
  onSuccess?: () => void
  onCloseDrawer?: () => void
}

/**
 * TanStack mutation hook for reviewing (approve / reject) a leave application.
 */
export function useReviewLeaveRequest(callbacks?: ReviewLeaveRequestOptions) {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const branchId = resolveBranchId() || user?.branchId || ''

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string
      payload: ReviewLeaveApplicationPayload
    }) => {
      return reviewLeaveApplication(id, payload)
    },
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leave-applications'] })
      queryClient.invalidateQueries({
        queryKey: ['leave-application-detail', branchId, variables.id],
      })
      queryClient.invalidateQueries({ queryKey: ['leave-balances'] })
      queryClient.invalidateQueries({ queryKey: ['leave-balance-by-staff'] })

      const msg = (res as any)?.message || 'Leave request updated'
      showToastMessage(msg, 'success')
      callbacks?.onSuccess?.()
    },
    onError: (err: unknown) => {
      const responseStatus = (err as any)?.response?.status
      if (responseStatus === 401) {
        return
      }
      const message = getApiErrorMessage(err, 'Failed to review leave application')
      showToastMessage(message, 'error')

      const lower = message.toLowerCase()
      if (
        (responseStatus === 400 && lower.includes('already reviewed')) ||
        responseStatus === 404
      ) {
        queryClient.invalidateQueries({ queryKey: ['leave-applications'] })
        callbacks?.onCloseDrawer?.()
      }
    },
  })
}

/**
 * Main coordinator hook for the admin Leave Requests tab.
 */
export function useLeaveRequests() {
  const { user } = useAuthStore()
  const branchId = resolveBranchId() || user?.branchId || ''

  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')

  // Fetch real leave policies dropdown from GET /api/hrms/leave/policies/dropdown
  const { data: dropdownPolicies = [] } = useQuery({
    queryKey: ['leave-policies-dropdown', branchId || ''],
    queryFn: getLeavePoliciesDropdown,
    staleTime: 5 * 60 * 1000,
  })

  // Policy options derived from GET /api/hrms/leave/policies/dropdown, plus items fallback
  const policyLookupRef = useRef<Map<string, string>>(new Map())

  // Keep lookup map in sync with dropdown policies
  useEffect(() => {
    if (dropdownPolicies && dropdownPolicies.length > 0) {
      dropdownPolicies.forEach((p) => {
        if (p.id && p.name) {
          policyLookupRef.current.set(p.name.toLowerCase(), p.id)
          if (p.code) {
            policyLookupRef.current.set(p.code.toLowerCase(), p.id)
          }
        }
      })
    }
  }, [dropdownPolicies])

  const [cachedPolicies, setCachedPolicies] = useState<PolicyOption[]>(() => {
    return [
      { label: 'Type: All', value: '' },
      ...LEAVE_TYPE_FILTER_OPTIONS.filter((opt) => opt.value !== ''),
    ]
  })

  // Update policy options from dropdown policies
  useEffect(() => {
    if (dropdownPolicies && dropdownPolicies.length > 0) {
      const nextOptions: PolicyOption[] = [{ label: 'Type: All', value: '' }]
      const seen = new Set<string>()

      dropdownPolicies.forEach((item) => {
        if (item.id && !seen.has(item.id)) {
          seen.add(item.id)
          nextOptions.push({
            label: item.name,
            value: item.id,
          })
        }
      })

      setCachedPolicies(nextOptions)
    }
  }, [dropdownPolicies])

  // Resolve whether typeFilter is a policy ID or a policy name
  const resolvedLeavePolicyId = useMemo(() => {
    if (!typeFilter || typeFilter === 'All') return undefined
    if (isUuid(typeFilter)) return typeFilter
    // If not UUID, check if matched in our known lookup
    const foundId = policyLookupRef.current.get(typeFilter.toLowerCase())
    if (foundId) return foundId
    return undefined
  }, [typeFilter])

  // Build query params
  const queryParams = useMemo<LeaveApplicationsQueryParams>(() => {
    const params: LeaveApplicationsQueryParams = {
      page,
      limit,
    }
    if (statusFilter && statusFilter !== 'All') {
      params.status = statusFilter as LeaveApplicationStatus
    }
    if (resolvedLeavePolicyId) {
      params.leavePolicyId = resolvedLeavePolicyId
    }
    if (fromDate) {
      params.fromDate = fromDate
    }
    if (toDate) {
      params.toDate = toDate
    }
    return params
  }, [page, limit, statusFilter, resolvedLeavePolicyId, fromDate, toDate])

  const {
    data,
    isLoading: loading,
    isFetching,
    isError,
    error,
    refetch,
  } = useLeaveRequestsListQuery(queryParams, branchId)

  // Update policy options cache with any additional loaded items not in dropdown
  useEffect(() => {
    if (data?.items && data.items.length > 0) {
      setCachedPolicies((prev) => {
        const map = new Map<string, string>()
        prev.forEach((p) => {
          if (p.value) map.set(p.value, p.label)
        })

        data.items.forEach((item) => {
          const id = item.leavePolicyId || item.leavePolicy?.id
          const label = item.leavePolicy?.name || (item.leavePolicy as any)?.code
          if (id && label) {
            map.set(id, label)
            policyLookupRef.current.set(label.toLowerCase(), id)
          }
        })

        const nextOptions: PolicyOption[] = [{ label: 'Type: All', value: '' }]
        map.forEach((label, value) => {
          nextOptions.push({ label, value })
        })
        return nextOptions
      })
    }
  }, [data?.items])

  const requests: StaffLeaveRequest[] = useMemo(() => {
    if (!data?.items) return []
    return data.items.map(mapLeaveApplicationToLeaveRequest)
  }, [data?.items])

  // Client-side fallback filter if type was specified by name without a matching policy ID
  const filteredRequests = useMemo(() => {
    if (typeFilter && !resolvedLeavePolicyId && typeFilter !== 'All') {
      const lower = typeFilter.toLowerCase()
      return requests.filter((r) => r.leaveType.toLowerCase() === lower)
    }
    return requests
  }, [requests, typeFilter, resolvedLeavePolicyId])

  const handleFromDateChange = useCallback(
    (date: string) => {
      setFromDate(date)
      setPage(1)
      if (date && toDate && date > toDate) {
        setToDate('')
      }
    },
    [toDate]
  )

  const handleToDateChange = useCallback((date: string) => {
    setToDate(date)
    setPage(1)
  }, [])

  const handleTypeFilterChange = useCallback((val: string) => {
    const clean = !val || val === 'All' || val === 'Type: All' ? '' : val
    setTypeFilter(clean)
    setPage(1)
  }, [])

  const handleStatusFilterChange = useCallback((val: string) => {
    const clean = !val || val === 'All' || val === 'Status: All' ? '' : val
    setStatusFilter(clean)
    setPage(1)
  }, [])

  const handleClearFilters = useCallback(() => {
    setTypeFilter('')
    setStatusFilter('')
    setFromDate('')
    setToDate('')
    setPage(1)
  }, [])

  const reviewMutation = useReviewLeaveRequest()

  const handleApproveRequest = useCallback(
    async (id: string, remarks?: string) => {
      return reviewMutation.mutateAsync({
        id,
        payload: {
          status: 'APPROVED',
          reviewRemarks: remarks,
        },
      })
    },
    [reviewMutation]
  )

  const handleRejectRequest = useCallback(
    async (id: string, reason: string, remarks?: string) => {
      return reviewMutation.mutateAsync({
        id,
        payload: {
          status: 'REJECTED',
          rejectionReason: reason,
          reviewRemarks: remarks,
        },
      })
    },
    [reviewMutation]
  )

  const handleExport = useCallback(() => {
    const todayStr = new Date().toISOString().split('T')[0]
    const fileName = `leave-requests-${todayStr}.xlsx`

    const exportRows = filteredRequests.map((r) => {
      const daysVal = parseInt(calculateDays(r.from, r.to), 10) || 1
      return {
        Employee: r.name,
        'Employee ID': r.employeeId,
        Department: r.department,
        'Leave Type': r.leaveType,
        From: formatDateDisplay(r.from),
        To: formatDateDisplay(r.to),
        Days: daysVal,
        Status: r.status,
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(exportRows)
    worksheet['!cols'] = [
      { wch: 24 },
      { wch: 16 },
      { wch: 20 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 10 },
      { wch: 14 },
    ]
    worksheet['!views'] = [{ state: 'frozen', ySplit: 1 }]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leave Requests')
    XLSX.writeFile(workbook, fileName)
  }, [filteredRequests])

  return {
    loading,
    isFetching,
    isError,
    error,
    refetch,
    page,
    setPage,
    limit,
    setLimit,
    total: data?.total ?? requests.length,
    typeFilter,
    setTypeFilter: handleTypeFilterChange,
    statusFilter,
    setStatusFilter: handleStatusFilterChange,
    fromDate,
    handleFromDateChange,
    toDate,
    handleToDateChange,
    handleClearFilters,
    filteredRequests,
    requests,
    policyOptions: cachedPolicies,
    handleExport,
    handleApproveRequest,
    handleRejectRequest,
    isReviewing: reviewMutation.isPending,
  }
}

